'use client'

import CoverageBlock from '@/components/ui/CoverageBlock'
import { selectionCoverage } from '@/components/ui/SelectionLabel'
import { getBiasCategory } from '@/utils/bias'
import { compositionDisplay } from '@/utils/composition'
import ArticlesCountTrigger from './ArticlesCountTrigger'
import LeanBarInfo from './LeanBarInfo'

// The story page's coverage, under the headline: the shared CoverageBlock
// (each side's count over its segment, "222 articles · Pano rates the
// coverage Leans Right ⓘ") with the commentary line under it.
const LEANS = [{ key: 'left' }, { key: 'center' }, { key: 'right' }]

// Whole percentages that add up to 100 (largest remainder), so the line
// under the bar never reads 34% · 33% · 34%.
export function percents(counts, total) {
  const raw = LEANS.map(({ key }) => (counts[key] / total) * 100)
  const out = raw.map(Math.floor)
  const order = raw.map((v, i) => [v - out[i], i]).sort((a, b) => b[0] - a[0])
  const short = 100 - out.reduce((a, b) => a + b, 0)
  for (let k = 0; k < short; k++) out[order[k][1]] += 1
  return Object.fromEntries(LEANS.map(({ key }, i) => [key, out[i]]))
}

export function leanCounts(articles = []) {
  const counts = { left: 0, center: 0, right: 0 }
  for (const a of articles) {
    const bias = a.outlet?.bias
    const key = typeof bias === 'number' ? getBiasCategory(bias) : 'center'
    if (key in counts) counts[key] += 1
  }
  return counts
}

// The commentary line under the bar (the mocks, 2026-11-01): the pipeline's
// composition verdict, then the counts by article type. "Some commentary:
// 104 news reports, 93 analysis pieces, 15 opinion pieces" on a desktop;
// "Some commentary: 104 news, 93 analysis, 15 opinion" on a phone. Nothing
// when the story has no composition (older stories).
// "33 news reports, 6 analysis pieces and 2 opinion pieces" (2026-11-01:
// the counts alone, without the "Reporting:" verdict in front).
const joinList = (items) =>
  items.length < 2
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`

function commentaryLines(topic) {
  const heat = compositionDisplay(topic?.composition, topic?.category)
  if (!heat) return null
  const n = heat.counts
  const long = []
  const short = []
  if (n.news) {
    long.push(`${n.news} news report${n.news === 1 ? '' : 's'}`)
    short.push(`${n.news} news`)
  }
  if (n.analysis) {
    long.push(`${n.analysis} analysis piece${n.analysis === 1 ? '' : 's'}`)
    short.push(`${n.analysis} analysis`)
  }
  if (n.opinion) {
    long.push(`${n.opinion} opinion piece${n.opinion === 1 ? '' : 's'}`)
    short.push(`${n.opinion} opinion`)
  }
  if (!long.length) return null
  return {
    long: joinList(long),
    short: joinList(short),
  }
}

export default function CoverageBand({ topic, counts: given, meta, caption }) {
  const counts = given || leanCounts(topic?.articles)
  const total = counts.left + counts.center + counts.right
  if (!total) return null
  const node = topic || { lean: counts, article_count: total }
  // The ⓘ explainer reads the shares.
  const pct = percents(counts, total)

  // The story page's coverage (2026-11-01): the coverage block every page
  // opens with (CoverageBlock: each side's count over its segment, then
  // "222 articles · Pano rates the coverage Leans Right ⓘ"), with the
  // commentary line under it.
  const commentary = commentaryLines(topic)
  return (
    <CoverageBlock
      counts={counts}
      articles={meta || <ArticlesCountTrigger />}
      label={caption ? null : selectionCoverage(node)?.text}
      info={
        caption ? null : (
          <LeanBarInfo
            total={total}
            pct={pct}
            label={selectionCoverage(node)}
          />
        )
      }
      note={
        commentary && (
          <>
            <span className="hidden sm:inline">{commentary.long}</span>
            <span className="sm:hidden">{commentary.short}</span>
          </>
        )
      }
      className="mx-auto mt-6 max-w-[760px] sm:mt-[34px]"
    />
  )
}
